import { FC, useEffect, useRef, useState } from "react";
import {
  FormController,
  MuiBox,
  MuiButton,
  MuiGrid,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
} from "../../../../components";
import { ClientSearchResultWrapper } from "./ClientSearchResult.style";

import SearchIcon from "@mui/icons-material/Search";

import { InputAdornment, Radio } from "@mui/material";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { ReactComponent as EditIcon } from "../../../../assets/images/edit.svg";
import { ReactComponent as HierarchyIcon } from "../../../../assets/images/hierarchy.svg";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { MuiLink } from "../../../../components/link/MuiLink";
import PermissionWrapper from "../../../../components/permissionWrapper/PermissionWrapper";
import { MODULE_PERMISSIONS, PAGE_URLS } from "../../../../helpers/constants";
import { MAX_LENGTH_50 } from "../../../../helpers/constants/validations";
import { LabelValue } from "../../../../helpers/types/common";
import { getValueOrBlank, isNotEmpty } from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { setCreateClientData } from "../../../../redux/slices/client/createClientSlice";
import {
  ClientResultRowValue,
  ClientSearchResultFormKey,
} from "../../modelsAndSchemas/Models";
import { clientSearchSiteCoreKeys } from "../../siteCore/SiteCoreKeys";

const pageId = "client-search-result";

interface ClientResultFormProps {
  rows: ClientResultRowValue[];
  isSearch: boolean;
  showAsModal?: boolean;
  onRowSelected?: (row: RowProps) => void;
  handleHierarchyClick?: (id: string) => void;
  onEditClick?: (id: string) => void;
  handleViewClientClick?: (id: string) => void;
}

export const ClientSearchResult: FC<ClientResultFormProps> = ({
  rows,
  isSearch,
  showAsModal,
  onRowSelected,
  handleHierarchyClick,
  handleViewClientClick,
  onEditClick,
}) => {
  const dispatch = useDispatch();
  const tableRef = useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const {
    fetchLabel,
    commonLabels,
    fetchComponentTitle,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();

  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [uniqueCountries, setUniqueCountries] = useState<LabelValue[]>([]);
  const [uniqueClientType, setUniqueClientType] = useState<LabelValue[]>([]);
  const [selectedRow, setSelectedRow] = useState<string>("");

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    if (tableRef.current) {
      tableRef.current.resetPagination(shouldResetRowsPerPage);
    }
  };

  useEffect(() => {
    if (Array.isArray(rows)) {
      rows.forEach((result) => {
        result.companyName = result.name ?? "";
        result.addressCountry = result.addressCountryName ?? "";
        result.clientType = result.clientTypeId ?? "";
      });
    }

    const uniqueCountriesData = Array.from(
      new Set(rows.map((item) => item.addressCountry).filter(Boolean)),
    ).map((country) => ({
      value: country as string,
      label: country as string,
    }));
    setUniqueCountries(uniqueCountriesData);

    const uniqueClientType = Array.from(
      new Set(
        rows
          .filter((item) => item.clientTypeId)
          .map((item) => item.clientTypeId),
      ),
    ).map((clientTypeId) => ({
      value: getValueOrBlank(clientTypeId),
      label: fetchValueByKeyOfDropdown(
        clientSearchSiteCoreKeys.searchFormKey,
        clientSearchSiteCoreKeys.clientTypeList,
        getValueOrBlank(clientTypeId),
      ),
    }));

    setUniqueClientType(
      uniqueClientType.filter((item) => isNotEmpty(item.label)),
    );

    reset();
    resetPagination(true);

    setFilteredResults([...rows]);
  }, [rows]);

  const { control, watch, reset, getValues } = useForm({
    mode: "all",
    defaultValues: {
      [ClientSearchResultFormKey.isSelected]: false,
      [ClientSearchResultFormKey.companyName]: "",
      [ClientSearchResultFormKey.clientCode]: "",
      [ClientSearchResultFormKey.clientType]: "",
      [ClientSearchResultFormKey.addressCountry]: "",
      [ClientSearchResultFormKey.city]: "",
      [ClientSearchResultFormKey.tablePaginationPageNo]: 1,
    },
  });

  useEffect(() => {
    // Logic to filter the records based on the search params in the result section
    const { companyName, clientCode, city, addressCountry, clientType } =
      getValues();
    const myData = [...rows].filter((item) => {
      return (
        item.companyName.toLowerCase().includes(companyName.toLowerCase()) &&
        item.clientCode.toLowerCase().includes(clientCode.toLowerCase()) &&
        (item.clientType ?? "")
          .toLowerCase()
          .includes(clientType.toLowerCase()) &&
        item.city &&
        item.city.toLowerCase().includes(city.toLowerCase()) &&
        (addressCountry.toLowerCase() ===
          commonLabels.selectPlaceHolder?.toLowerCase() ||
          item.addressCountry
            ?.toLowerCase()
            .includes(addressCountry.toLowerCase()))
      );
    });

    setFilteredResults([...myData]);
    resetPagination();
  }, [
    watch(ClientSearchResultFormKey.companyName),
    watch(ClientSearchResultFormKey.clientCode),
    watch(ClientSearchResultFormKey.clientType),
    watch(ClientSearchResultFormKey.city),
    watch(ClientSearchResultFormKey.addressCountry),
  ]);

  const setClientDataInRedux = (row: any) => {
    dispatch(
      setCreateClientData({
        id: row.id,
        clientCode: row.clientCode,
        clientName: row.clientName ?? "",
      }),
    );
  };

  const renderCompanyName = (companyName: string) => {
    return (
      <MuiBox data-testid="company-name-link">
        <MuiEllipseLabel
          data-testid="company-name-link"
          labelTypoClassName="Link company-name-link"
          text={companyName}
          labelTypoVariant="h3"
          placement="bottom-start"
          pointerCursor={!showAsModal}
        />
      </MuiBox>
    );
  };

  const handleRowSelected = (row: RowProps) => {
    setSelectedRow(row.id as string);
    onRowSelected?.(row);
  };

  const columns = [
    ...(showAsModal
      ? [
          {
            id: ClientSearchResultFormKey.isSelected,
            label: "",
            isSortVisible: false,
            render: (row: RowProps) => {
              return (
                <Radio
                  data-testid={`radio-${row.id}`}
                  checked={selectedRow === row.id}
                  onChange={() => handleRowSelected(row)}
                  aria-label={commonARIALabel?.selectRow}
                  aria-hidden
                />
              );
            },
          },
        ]
      : []),
    {
      id: ClientSearchResultFormKey.companyName,
      label: fetchLabel(
        clientSearchSiteCoreKeys.result,
        clientSearchSiteCoreKeys.companyNameHeader,
      ),
      isSortVisible: true,
      render: (row: any) => {
        if (!row.companyName) return null;

        let content = renderCompanyName(row.companyName);

        if (!showAsModal) {
          content = (
            <MuiLink
              data-testid="company-name-link"
              href={`${PAGE_URLS.VIEW_CLIENT}/${row.id}`}
              isOpenNewTab
            >
              {renderCompanyName(row.companyName)}
            </MuiLink>
          );
        }
        return content;
      },
    },
    {
      id: ClientSearchResultFormKey.clientType,
      label: fetchLabel(
        clientSearchSiteCoreKeys.result,
        clientSearchSiteCoreKeys.clientTypeResults,
      ),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            {fetchValueByKeyOfDropdown(
              clientSearchSiteCoreKeys.searchFormKey,
              clientSearchSiteCoreKeys.clientTypeList,
              row.clientType,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ClientSearchResultFormKey.clientCode,
      label: fetchLabel(
        clientSearchSiteCoreKeys.result,
        clientSearchSiteCoreKeys.clientCodeHeader,
      ),
      isSortVisible: true,
    },
    {
      id: ClientSearchResultFormKey.addressCountry,
      label: fetchLabel(
        clientSearchSiteCoreKeys.result,
        clientSearchSiteCoreKeys.countryHeader,
      ),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: ClientSearchResultFormKey.city,
      label: fetchLabel(
        clientSearchSiteCoreKeys.result,
        clientSearchSiteCoreKeys.cityHeader,
      ),
      isSortVisible: true,
      className: "cell-city-type",
    },
    {
      id: "hierarchy",
      label: fetchLabel(
        clientSearchSiteCoreKeys.result,
        clientSearchSiteCoreKeys.hierarchyHeader,
      ),
      render: (row: RowProps) => {
        return (
          <MuiButton
            aria-label={commonARIALabel.viewHierarchy}
            onClick={() => handleHierarchyClick?.(row.id)}
            data-testid="hierarchy-link"
          >
            <HierarchyIcon aria-hidden />
          </MuiButton>
        );
      },
    },
    {
      id: "edit",
      label: commonLabels.edit,
      render: (row: RowProps) => {
        return (
          <PermissionWrapper
            hasCountryLevelAccess={row?.isEditableForCountry}
            permission={MODULE_PERMISSIONS.EDIT_CLIENT}
          >
            <MuiButton
              aria-label={commonARIALabel.clientResultEditIcon}
              data-testid="edit-link"
              onClick={() => {
                setClientDataInRedux(row);
                onEditClick?.(row.id);
              }}
            >
              <EditIcon aria-hidden />
            </MuiButton>
          </PermissionWrapper>
        );
      },
    },
  ];

  const tableHeaderForm = [
    ...(showAsModal ? [<></>] : []),
    <FormController
      key={ClientSearchResultFormKey.companyName}
      control={control}
      name={ClientSearchResultFormKey.companyName}
    >
      <MuiTextField
        id={`${pageId}-${ClientSearchResultFormKey.companyName}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        data-testid="company-input"
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key={ClientSearchResultFormKey.clientType}
      control={control}
      name={ClientSearchResultFormKey.clientType}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueClientType}
        id={`${pageId}-${ClientSearchResultFormKey.clientType}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key={ClientSearchResultFormKey.clientCode}
      control={control}
      name={ClientSearchResultFormKey.clientCode}
    >
      <MuiTextField
        id={`${pageId}-${ClientSearchResultFormKey.clientCode}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      key={ClientSearchResultFormKey.addressCountry}
      control={control}
      name={ClientSearchResultFormKey.addressCountry}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueCountries}
        value=""
        id={`${pageId}-${ClientSearchResultFormKey.addressCountry}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
        showSearch
        searchPlaceholder={commonLabels.search}
      />
    </FormController>,
    <FormController
      key={ClientSearchResultFormKey.city}
      control={control}
      name={ClientSearchResultFormKey.city}
    >
      <MuiTextField
        id={`${pageId}-${ClientSearchResultFormKey.city}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
    <></>,
  ];

  const getColumns = () => {
    return showAsModal ? columns.slice(0, -2) : columns;
  };

  const getHeaderForm = () => {
    return showAsModal ? tableHeaderForm.slice(0, -2) : tableHeaderForm;
  };

  return (
    <ClientSearchResultWrapper>
      <MuiGrid container data-testid="client-result">
        <MuiGrid size={{ xs: 12 }} className="form-header">
          <MuiTypography variant="h2SemiBold" className="form-header-left">
            {fetchComponentTitle(clientSearchSiteCoreKeys.result)}
          </MuiTypography>
        </MuiGrid>
        <MuiGrid container>
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={getHeaderForm()}
              columns={getColumns()}
              tableResults={[...filteredResults]}
              noDataInApi={rows.length === 0 && !isSearch}
              onRowSelected={handleRowSelected}
              emptyMessage={commonLabels.applyNecessaryFiltersMessage}
              noResultMessage={commonLabels.noResult}
              priorityResults={[]}
              isLastColumnSticky={!showAsModal}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
    </ClientSearchResultWrapper>
  );
};
